import ContentLoader from 'react-content-loader'
import styled, { useTheme } from 'styled-components'

import { PositionInfo } from '@/shared/kai/hooks/useUserPositions'
import { USDC } from '@kunalabs-io/kai'

import { Button } from 'components/Button/Button'
import { IconInfo } from 'components/Icons/InfoIcon'
import { Tooltip } from 'components/Tooltip/Tooltip'
import { Typography } from 'components/Typography/Typography'
import { useRewardsFeesTotalValue } from './hooks/useRewardsFeesTotalValue'
import { useRewardsFeesInfo } from './hooks/useRewardFeeInfos'
import { useIsMobile } from 'utils/useIsMobile'
import { getFormattedUsdValue } from 'utils/usdFormatters'

interface Props {
  positionInfo: PositionInfo
  openClaimDialog: () => void
  positionStatus: 'active' | 'inactive' | 'closed'
}

const ErrorLabel = styled(Typography)`
  color: ${p => p.theme.colors.text.danger};
`

const TooltipSectionLabel = styled(Typography)`
  display: flex;
  align-items: center;
  margin-bottom: 12px;

  @media screen and (max-width: ${p => p.theme.breakpoints.md}px) {
    & > svg {
      margin-left: 6px;
    }
  }

  & > div {
    display: flex;
    cursor: pointer;
    & > svg {
      margin-left: 6px;
      path {
        stroke: ${p => p.theme.colors.text.secondary};
      }
    }
  }
`

const PendingFeesRewardsTotalContentLoader = () => {
  const theme = useTheme()

  return (
    <ContentLoader
      speed={2}
      width={130}
      height={32}
      viewBox="0 0 130 32"
      backgroundColor={theme.colors.contentLoader.backgroundColor}
      foregroundColor={theme.colors.contentLoader.foregroundColor}
    >
      <rect x="0" y="0" rx="4" ry="4" width="130" height="32" />
    </ContentLoader>
  )
}

export const PendingFeesRewardsTotalClosed = () => {
  const isMobile = useIsMobile()

  return (
    <>
      <TooltipSectionLabel variant="label2" color="secondary" fontWeight="medium">
        Pending rewards & fees
        {isMobile ? (
          <IconInfo width={16} height={16} />
        ) : (
          <Tooltip
            content="Unclaimed earnings from liquidity provision, including swap fees and additional incentives."
            position="top"
          >
            <IconInfo width={16} height={16} />
          </Tooltip>
        )}
      </TooltipSectionLabel>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <Typography variant="label1" fontWeight="bold">
          $0
        </Typography>
      </div>
    </>
  )
}

export const PendingFeesRewardsTotal = ({ positionInfo, openClaimDialog, positionStatus }: Props) => {
  const {
    result: rewardsFeesInfos,
    isLoading: isLoadingRewardsFees,
    error: rewardsFeesInfoError,
  } = useRewardsFeesInfo(positionInfo)

  const { isLoading: isLoadingTotalValue, totalSum } = useRewardsFeesTotalValue(positionInfo, rewardsFeesInfos, USDC)

  const isMobile = useIsMobile()

  const isLoading = isLoadingRewardsFees || isLoadingTotalValue || rewardsFeesInfos === null

  return (
    <>
      <TooltipSectionLabel variant="label2" color="secondary" fontWeight="medium">
        Pending rewards & fees
        {isMobile ? (
          <IconInfo width={16} height={16} />
        ) : (
          <Tooltip
            content="Unclaimed earnings from liquidity provision, including swap fees and additional incentives."
            position="top"
          >
            <IconInfo width={16} height={16} />
          </Tooltip>
        )}
      </TooltipSectionLabel>
      {isLoading ? (
        <PendingFeesRewardsTotalContentLoader />
      ) : rewardsFeesInfoError ? (
        <ErrorLabel variant="body">Something went wrong while loading fees and rewards</ErrorLabel>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          {typeof totalSum !== 'undefined' ? (
            <>
              <Typography variant="label1" fontWeight="bold">
                {`(${getFormattedUsdValue(totalSum.toNumber())})`}
              </Typography>
              {positionStatus === 'active' ? (
                <Button outlined style={{ padding: '6px 16px', marginLeft: 8 }} onClick={openClaimDialog}>
                  <Typography variant="body" fontWeight="semiBold">
                    Claim
                  </Typography>
                </Button>
              ) : null}
            </>
          ) : null}
        </div>
      )}
    </>
  )
}
